/**
 * DemandZoneBadge - Visual urgency indicator for signals
 * 
 * DEX Standards:
 * - Graphics > Words: Visual pulse for urgency
 * - 5-Word Filter: Labels are 2 words max
 * - No emojis: Uses animated dot
 * - 700ms transitions: Smooth state changes
 * - Playfair Display: Typography consistency
 */

import React from 'react';

type DemandZone = 'HIGH' | 'MEDIUM' | 'LOW' | 'act_now' | 'perfect' | 'nurture' | string;

interface DemandZoneBadgeProps {
  zone: DemandZone;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

// Normalize zone values from different sources
const normalizeZone = (zone: DemandZone): 'HIGH' | 'MEDIUM' | 'LOW' => {
  const normalized = zone?.toLowerCase?.() || 'medium';
  if (normalized === 'act_now' || normalized === 'high') return 'HIGH';
  if (normalized === 'perfect' || normalized === 'medium') return 'MEDIUM';
  if (normalized === 'nurture' || normalized === 'low') return 'LOW';
  return 'MEDIUM';
};

const ZONE_CONFIG = {
  HIGH: {
    bg: 'bg-red-500',
    text: 'text-red-500',
    border: 'border-red-500',
    label: 'Act Now',       // 2 words
    pulse: true,
  },
  MEDIUM: {
    bg: 'bg-amber-500',
    text: 'text-amber-500',
    border: 'border-amber-500',
    label: 'This Week',     // 2 words
    pulse: false,
  },
  LOW: {
    bg: 'bg-slate-400',
    text: 'text-slate-400',
    border: 'border-slate-400',
    label: 'Nurture',       // 1 word
    pulse: false,
  },
};

export function DemandZoneBadge({ 
  zone, 
  size = 'md', 
  showLabel = true 
}: DemandZoneBadgeProps) {
  const normalizedZone = normalizeZone(zone);
  const config = ZONE_CONFIG[normalizedZone];
  
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };
  
  const dotSizes = {
    sm: 'w-1 h-1',
    md: 'w-1.5 h-1.5',
    lg: 'w-2 h-2',
  };

  return (
    <div 
      className={`inline-flex items-center gap-1.5 ${config.bg} rounded-full ${sizeClasses[size]} transition-all duration-700`}
    >
      {config.pulse && (
        <span className={`${dotSizes[size]} bg-white rounded-full animate-pulse`} />
      )}
      {showLabel && (
        <span 
          className="font-bold text-white"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {config.label}
        </span>
      )}
    </div>
  );
}

// Outline variant for use on dark backgrounds
export function DemandZoneBadgeOutline({ 
  zone, 
  size = 'md' 
}: Omit<DemandZoneBadgeProps, 'showLabel'>) {
  const normalizedZone = normalizeZone(zone);
  const config = ZONE_CONFIG[normalizedZone];
  
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm',
  };

  return (
    <div 
      className={`inline-flex items-center gap-1 border ${config.border} rounded-full ${sizeClasses[size]} transition-all duration-700`}
    >
      {config.pulse && (
        <span className={`w-1.5 h-1.5 ${config.bg} rounded-full animate-pulse`} />
      )}
      <span 
        className={`font-semibold ${config.text}`}
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {config.label}
      </span>
    </div>
  );
}

export default DemandZoneBadge;
